import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { setBranchId } from '../../../auth/actions';
import { setFiltersVisibility } from '../../../../common/actions';
import PropTypes from 'prop-types';
import DateRangePicker from '../../../../common/components/DateRangePicker';
import { Button, Dropdown, Loading, LineChartCard } from '../../../../common/ui';
import { useSearchParams } from 'react-router-dom';
import PieChartCard from '../../../dashboard/components/PieChartCard';
import SalesFilterComp from './SalesFilterComp';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import {
  fetchSalesSummary,
  fetchSalesChartData,
  fetchPaymentMethodBreakdown,
  fetchRestaurantSalesSummary,
  fetchCompanyType,
  exportSalesReportExcel,
  fetchSalesBusinessDays,
} from '../actions';
import { findBusinessDaySettings } from '../../../settings/businessDay/actions';
import store from '../../../../store';
import { formatBusinessVerticalLabel } from '../../billHistory/utils/businessVerticalLabels';

const TAB_KEYS = { SUMMARY: 'summary', DAILY: 'daily', MONTHLY: 'monthly' };
const COMPANY_TYPE_RESTAURANT = 'RESTAURANT';

const TAB_LABELS = {
  [TAB_KEYS.SUMMARY]: 'Summary',
  [TAB_KEYS.DAILY]: 'Daily Sales',
  [TAB_KEYS.MONTHLY]: 'Monthly Sales',
};

const ZONE_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const startOfLocalDayMs = (ymd) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) - ZONE_OFFSET_MS;
};

const overlapsCalendarDate = (day, ymd) => {
  const startOfSpan = startOfLocalDayMs(ymd);
  if (startOfSpan == null) return false;
  const endOfSpan = startOfSpan + DAY_MS;
  const start = Number(day.startDatetime);
  if (!Number.isFinite(start)) return false;
  const end =
    day.endDatetime == null || day.endDatetime === '' ? null : Number(day.endDatetime);
  return start < endOfSpan && (end == null || end >= startOfSpan);
};

const defaultBusinessDayId = (days, ymd) => {
  const match = (days || [])
    .filter((day) => overlapsCalendarDate(day, ymd))
    .sort((a, b) => Number(a.startDatetime) - Number(b.startDatetime))[0];
  return match ? String(match.id) : '';
};

const formatBusinessDayOption = (day) => {
  const shifted = new Date(Number(day.startDatetime) + ZONE_OFFSET_MS);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let hour = shifted.getUTCHours();
  const minute = String(shifted.getUTCMinutes()).padStart(2, '0');
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour %= 12;
  if (hour === 0) hour = 12;
  const statusLabel = String(day.status || '').toUpperCase() === 'OPEN' ? 'Open' : 'Sealed';
  return `${shifted.getUTCDate()} ${months[shifted.getUTCMonth()]} ${shifted.getUTCFullYear()}, ${hour}:${minute} ${ampm} ${statusLabel}`;
};

const getThisMonthDates = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    fromDate: firstDay.toLocaleDateString('en-CA'),
    toDate: lastDay.toLocaleDateString('en-CA'),
  };
};

const PAYMENT_METHOD_COLORS = ['#095664', '#10B2D0', '#98D0DA', '#BFE8F2', '#E5E7EB'];

const formatLKR = (value) =>
  `LKR ${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const isRestaurantCompany = (companyType) => {
  if (!companyType) return false;
  return String(companyType).trim().toUpperCase() === COMPANY_TYPE_RESTAURANT;
};

const SalesReport = ({
  setBranchId,
  setFiltersVisibility,
  findBusinessDaySettings,
  auth: { branchList = [], branchId, companyId, companyType: authCompanyType },
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultDates = getThisMonthDates();
  const fromDateParam = searchParams.get('fromDate') || defaultDates.fromDate;
  const toDateParam = searchParams.get('toDate') || defaultDates.toDate;
  const fromDate = fromDateParam || new Date().toLocaleDateString('en-CA');
  const toDate = toDateParam || new Date().toLocaleDateString('en-CA');
  const periodType = searchParams.get('periodType') || '';
  const startBusinessDayId = searchParams.get('startBusinessDayId') || '';
  const endBusinessDayId = searchParams.get('endBusinessDayId') || '';
  const [businessDayEnabled, setBusinessDayEnabled] = useState(null);
  const [businessDays, setBusinessDays] = useState([]);
  const [businessDaysLoaded, setBusinessDaysLoaded] = useState(false);
  const spanRef = useRef(`${fromDate}|${toDate}`);
  const branchRef = useRef(branchId);

  const [activeTab, setActiveTab] = useState(TAB_KEYS.SUMMARY);
  const [summaryMetrics, setSummaryMetrics] = useState([]);
  const [restaurantMetrics, setRestaurantMetrics] = useState([]);
  const [resolvedCompanyType, setResolvedCompanyType] = useState(authCompanyType);
  const [chartData, setChartData] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [restaurantLoading, setRestaurantLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [cashierResetTrigger, setCashierResetTrigger] = useState(false);
  const [userResetTrigger, setUserResetTrigger] = useState(false);
  const [customerResetTrigger, setCustomerResetTrigger] = useState(false);

  const businessDayMode = businessDayEnabled === true && periodType === 'businessDay';
  const reportReady =
    businessDayEnabled === false ||
    (businessDayEnabled === true && periodType === 'dateRange') ||
    (businessDayMode && Boolean(startBusinessDayId) && Boolean(endBusinessDayId));

  const buildSharedParams = useCallback(() => {
    const params = { fromDate, toDate };
    if (businessDayMode && startBusinessDayId && endBusinessDayId) {
      params.periodType = 'businessDay';
      params.startBusinessDayId = startBusinessDayId;
      params.endBusinessDayId = endBusinessDayId;
    }
    if (filters.cashier && filters.cashier.id != null) {
      params.cashierId = filters.cashier.id;
    } else if (filters.user && filters.user.id != null) {
      params.cashierId = filters.user.id;
    }
    if (filters.customer && filters.customer.id != null) {
      params.customerId = filters.customer.id;
    }
    return params;
  }, [
    fromDate,
    toDate,
    businessDayMode,
    startBusinessDayId,
    endBusinessDayId,
    filters.cashier,
    filters.user,
    filters.customer,
  ]);

  const buildVerticalParams = useCallback(() => {
    const params = buildSharedParams();
    if (filters.businessVertical) {
      params.businessVertical = filters.businessVertical;
    }
    return params;
  }, [buildSharedParams, filters.businessVertical]);

  useEffect(() => {
    if (authCompanyType) {
      setResolvedCompanyType(authCompanyType);
      return;
    }
    if (!companyId) {
      setResolvedCompanyType(null);
      return;
    }
    fetchCompanyType().then((type) => setResolvedCompanyType(type));
  }, [companyId, authCompanyType]);

  const isRestaurant = isRestaurantCompany(resolvedCompanyType);

  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setBusinessDayEnabled(false);
      return undefined;
    }
    setBusinessDayEnabled(null);
    findBusinessDaySettings(branchId).then((ok) => {
      if (cancelled) return;
      if (!ok) {
        setError('Failed to load Business Day settings');
        return;
      }
      const rows = store.getState().businessDaySettingsState?.businessDaySettingsData || [];
      const enabled = rows.some(
        (row) =>
          row.param === 'is-business-day-enabled' &&
          String(row.value).toLowerCase() === 'true'
      );
      setBusinessDayEnabled(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [branchId, findBusinessDaySettings]);

  useEffect(() => {
    if (businessDayEnabled == null) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!businessDayEnabled) {
        let changed = false;
        ['periodType', 'startBusinessDayId', 'endBusinessDayId'].forEach((key) => {
          if (next.has(key)) {
            next.delete(key);
            changed = true;
          }
        });
        return changed ? next : prev;
      }
      const period = next.get('periodType');
      if (period !== 'businessDay' && period !== 'dateRange') {
        next.set('periodType', 'businessDay');
        next.delete('startBusinessDayId');
        next.delete('endBusinessDayId');
        return next;
      }
      return prev;
    });
  }, [businessDayEnabled, branchId, setSearchParams]);

  useEffect(() => {
    if (branchRef.current === branchId) return;
    branchRef.current = branchId;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('startBusinessDayId');
      next.delete('endBusinessDayId');
      return next;
    });
  }, [branchId, setSearchParams]);

  useEffect(() => {
    const spanKey = `${fromDate}|${toDate}`;
    if (spanRef.current === spanKey) return;
    spanRef.current = spanKey;
    if (periodType !== 'businessDay') return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('startBusinessDayId');
      next.delete('endBusinessDayId');
      return next;
    });
  }, [fromDate, toDate, periodType, setSearchParams]);

  useEffect(() => {
    if (!businessDayMode || !companyId || !branchId) {
      setBusinessDays([]);
      setBusinessDaysLoaded(false);
      return undefined;
    }
    let cancelled = false;
    setBusinessDaysLoaded(false);
    fetchSalesBusinessDays({ fromDate, toDate })
      .then((days) => {
        if (cancelled) return;
        const list = days || [];
        setBusinessDays(list);
        setBusinessDaysLoaded(true);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          let changed = false;
          const listedIds = new Set(list.map((day) => String(day.id)));
          ['startBusinessDayId', 'endBusinessDayId'].forEach((key) => {
            const current = next.get(key);
            if (current && !listedIds.has(current)) {
              next.delete(key);
              changed = true;
            }
          });
          if (!next.get('startBusinessDayId')) {
            const id = defaultBusinessDayId(list, fromDate);
            if (id) {
              next.set('startBusinessDayId', id);
              changed = true;
            }
          }
          if (!next.get('endBusinessDayId')) {
            const id = defaultBusinessDayId(list, toDate);
            if (id) {
              next.set('endBusinessDayId', id);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setBusinessDays([]);
          setBusinessDaysLoaded(false);
          setError(err?.message || 'Failed to load business days');
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('startBusinessDayId');
            next.delete('endBusinessDayId');
            return next;
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [businessDayMode, companyId, branchId, fromDate, toDate, setSearchParams]);

  useEffect(() => {
    if (!companyId || !branchId || !reportReady) {
      if (!reportReady) setSummaryMetrics([]);
      return;
    }
    setSummaryLoading(true);
    setError(null);
    fetchSalesSummary(buildVerticalParams())
      .then((s) => {
        setSummaryMetrics(s || []);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load report data');
        setSummaryMetrics([]);
      })
      .finally(() => setSummaryLoading(false));
  }, [companyId, branchId, buildVerticalParams, reportReady]);

  useEffect(() => {
    if (!companyId || !branchId || !reportReady) {
      if (!reportReady) setChartData([]);
      return;
    }
    setLoading(true);
    setError(null);
    fetchSalesChartData(buildSharedParams())
      .then((c) => {
        setChartData(c || []);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load report data');
        setChartData([]);
      })
      .finally(() => setLoading(false));
  }, [companyId, branchId, buildSharedParams, reportReady]);

  useEffect(() => {
    if (!companyId || !branchId || !reportReady) {
      if (!reportReady) setPaymentMethods([]);
      return;
    }
    setPaymentLoading(true);
    setError(null);
    fetchPaymentMethodBreakdown(buildVerticalParams())
      .then((p) => {
        setPaymentMethods(p || []);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load report data');
        setPaymentMethods([]);
      })
      .finally(() => setPaymentLoading(false));
  }, [companyId, branchId, buildVerticalParams, reportReady]);

  useEffect(() => {
    if (!companyId || !branchId || !isRestaurant || !reportReady) {
      setRestaurantMetrics([]);
      setRestaurantLoading(false);
      return;
    }
    setRestaurantLoading(true);
    fetchRestaurantSalesSummary(buildSharedParams())
      .then((metrics) => setRestaurantMetrics(metrics || []))
      .catch(() => setRestaurantMetrics([]))
      .finally(() => setRestaurantLoading(false));
  }, [companyId, branchId, buildSharedParams, isRestaurant, reportReady]);


  const handleDateRangeChange = (dateRange) => {
    if (dateRange.startDate && dateRange.endDate) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('fromDate', dateRange.startDate.toLocaleDateString('en-CA'));
        next.set('toDate', dateRange.endDate.toLocaleDateString('en-CA'));
        return next;
      });
    }
  };

  const handleExport = () => {
    if (!companyId || !branchId) {
      // Basic guard: export requires company & branch context
      // eslint-disable-next-line no-alert
      alert('Company and branch must be selected to export sales report.');
      return;
    }

    if (businessDayMode && (!startBusinessDayId || !endBusinessDayId)) return;
    exportSalesReportExcel(buildSharedParams())
      .then((response) => {
        const contentType =
          response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const disposition = response.headers['content-disposition'] || '';
        const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
        let filename = filenameMatch && filenameMatch[1]
          ? filenameMatch[1]
          : `Sales_${fromDate.replace(/-/g, '')}_${toDate.replace(/-/g, '')}.xlsx`;

        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert('Failed to export sales report.');
      });
  };

  const handleFiltersClick = () => {
    setFiltersVisibility(true);
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (key === 'cashier') setCashierResetTrigger((p) => !p);
    if (key === 'user') setUserResetTrigger((p) => !p);
    if (key === 'customer') setCustomerResetTrigger((p) => !p);
  };

  const handleResetFilters = () => {
    setFilters({});
    setCashierResetTrigger((p) => !p);
    setUserResetTrigger((p) => !p);
    setCustomerResetTrigger((p) => !p);
  };

  const tabLabel = TAB_LABELS[activeTab];
  const pageTitle = activeTab === TAB_KEYS.SUMMARY ? 'Summary Report' : `${tabLabel} Report`;
  const breadcrumb = `Reports / Sales / ${tabLabel}`;

  const filterTagLabel = (key, value) => {
    if (key === 'cashier') return `Cashier: ${value.name || value.cashierNo || value.id}`;
    if (key === 'user') return `User: ${value.name || value.id}`;
    if (key === 'customer') return `Customer: ${value.name || value.id}`;
    if (key === 'businessVertical') {
      return `Business Vertical: ${formatBusinessVerticalLabel(value) || value}`;
    }
    return `${key}: ${value}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>{pageTitle}</h1>
            <p className={styles.breadcrumb}>{breadcrumb}</p>
          </div>

          {branchId && (branchList || []).length > 0 && (
            <div className={styles.branchWrap}>
              <Dropdown
                label="Branch"
                options={(branchList || []).map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                value={branchId}
                onChange={(value) => setBranchId(value)}
                placeholder="Select branch"
              />
            </div>
          )}
        </div>

        <div className={styles.addedFiltersDiv}>
          {Object.entries(filters).map(([key, value]) => (
            <span key={key} className={styles.filterTag}>
              {filterTagLabel(key, value)}
              <button
                type="button"
                className={styles.removeFilterBtn}
                onClick={() => handleRemoveFilter(key)}
                aria-label="Remove filter"
              >
                <IoMdCloseCircleOutline />
              </button>
            </span>
          ))}
          {Object.keys(filters).length > 0 && (
            <button
              type="button"
              className={styles.resetFiltersBtn}
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>

      <div className={styles.tabsRow}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === TAB_KEYS.SUMMARY ? styles.tabActive : ''}`}
          onClick={() => setActiveTab(TAB_KEYS.SUMMARY)}
        >
          Summary
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === TAB_KEYS.DAILY ? styles.tabActive : ''}`}
          onClick={() => setActiveTab(TAB_KEYS.DAILY)}
        >
          Daily Sales
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === TAB_KEYS.MONTHLY ? styles.tabActive : ''}`}
          onClick={() => setActiveTab(TAB_KEYS.MONTHLY)}
        >
          Monthly Sales
        </button>
      </div>

      <div className={styles.actionsBar}>
        <div className={styles.actionsRow}>
          <div className={styles.dateRangeContainer}>
            <DateRangePicker
              onDateRangeChange={handleDateRangeChange}
              initialStartDate={new Date(fromDate)}
              initialEndDate={new Date(toDate)}
              businessDay={
                businessDayEnabled
                  ? {
                      periodType: periodType || 'businessDay',
                      onPeriodTypeChange: (value) => {
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('periodType', value);
                          next.delete('startBusinessDayId');
                          next.delete('endBusinessDayId');
                          return next;
                        });
                      },
                      options: businessDays.map((day) => ({
                        id: String(day.id),
                        label: formatBusinessDayOption(day),
                      })),
                      startId: startBusinessDayId,
                      endId: endBusinessDayId,
                      onStartChange: (id) => {
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('startBusinessDayId', id);
                          return next;
                        });
                      },
                      onEndChange: (id) => {
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('endBusinessDayId', id);
                          return next;
                        });
                      },
                      empty: businessDaysLoaded && businessDays.length === 0,
                    }
                  : null
              }
            />
          </div>
          <Button variant="secondary" onClick={handleFiltersClick}>
            Filters
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            Export
          </Button>
        </div>
      </div>

      <SalesFilterComp
        filters={filters}
        setFilters={setFilters}
        fromDate={fromDate}
        toDate={toDate}
        cashierResetTrigger={cashierResetTrigger}
        userResetTrigger={userResetTrigger}
        customerResetTrigger={customerResetTrigger}
      />

      <div className={styles.body}>
        {error && <div className={styles.errorMessage}>{error}</div>}
        {loading && activeTab !== TAB_KEYS.SUMMARY && (
          <Loading variant="inline" label="Loading..." />
        )}
        {activeTab === TAB_KEYS.SUMMARY && (
          <>
            {summaryLoading && <Loading variant="inline" label="Loading..." />}
            {!summaryLoading && (
              <div className={styles.summaryCardsGrid}>
                {summaryMetrics.map((m) => (
                  <div key={m.key} className={styles.summaryCard}>
                    <div className={styles.summaryCardLabel}>{m.label}</div>
                    <div className={styles.summaryCardValue}>
                      {formatLKR(m.value)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isRestaurant && (
              <div className={styles.restaurantSection}>
                <h2 className={styles.metricsSectionTitle}>Restaurant Summary</h2>
                {restaurantLoading && (
                  <Loading variant="inline" label="Loading restaurant summary..." />
                )}
                {!restaurantLoading && restaurantMetrics.length > 0 ? (
                  <div className={`${styles.summaryCardsGrid} ${styles.restaurantCardsGrid}`}>
                    {restaurantMetrics.map((m) => (
                      <div key={m.key} className={styles.summaryCard}>
                        <div className={styles.summaryCardLabel}>{m.label}</div>
                        <div className={styles.summaryCardValue}>
                          {formatLKR(m.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : !restaurantLoading ? (
                  <p className={styles.restaurantEmpty}>
                    No restaurant sales data for the selected period.
                  </p>
                ) : null}
              </div>
            )}

            {!loading && (
            <div className={styles.chartCard}>
              <div className={styles.chartCardTitle}>Sales Chart</div>
              <div className={styles.chartCardChart}>
                <LineChartCard data={chartData} noCard />
              </div>
            </div>
            )}

            {paymentLoading && <Loading variant="inline" label="Loading..." />}
            {!paymentLoading && (
            <div className={styles.paymentSection}>
              <div className={styles.paymentHeader}>
                <div className={styles.paymentTitle}>Sales By Payment Type</div>
                <div className={styles.paymentSubtitle}>
                  Comparison of Payment Method Contributions to Total Sales
                </div>
              </div>

              <div className={styles.paymentContent}>
                <div className={styles.paymentTiles}>
                  {paymentMethods.map((m) => (
                    <div key={m.key} className={styles.paymentTile}>
                      <div className={styles.paymentTileTop}>
                        <div className={styles.paymentTileLabel}>
                          {m.label.toUpperCase()} SALES
                        </div>
                        <div className={styles.paymentPercentBadge}>
                          {m.percent}%
                        </div>
                      </div>
                      <div className={styles.paymentTileAmount}>
                        {formatLKR(m.netSales)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.paymentChartArea}>
                  <div className={styles.paymentChartBox}>
                    <div className={styles.donutWrap}>
                      <PieChartCard
                        title=""
                        data={paymentMethods.map((m) => ({
                          label: m.label,
                          value: m.netSales,
                        }))}
                        colorArray={PAYMENT_METHOD_COLORS}
                        arcLabelMode="percent"
                        showLegend={false}
                      />
                    </div>
                    <div className={styles.paymentChartLegend}>
                      {paymentMethods.map((m, i) => (
                        <div key={m.key} className={styles.paymentChartLegendItem}>
                          <span
                            className={styles.paymentChartLegendDot}
                            style={{ backgroundColor: PAYMENT_METHOD_COLORS[i % PAYMENT_METHOD_COLORS.length] }}
                            aria-hidden
                          />
                          <span className={styles.paymentChartLegendLabel}>{m.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </>
        )}
        {!loading && activeTab !== TAB_KEYS.SUMMARY && (
          <div className={styles.placeholderBody}>
            {tabLabel} content will be implemented here.
          </div>
        )}
      </div>
    </div>
  );
};

SalesReport.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  setFiltersVisibility: PropTypes.func.isRequired,
  findBusinessDaySettings: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyType: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
});

export default connect(mapStateToProps, {
  setBranchId,
  setFiltersVisibility,
  findBusinessDaySettings,
})(SalesReport);
